import React, { useState } from 'react';
import { Users, BarChart3, Clock, Database, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import * as fmt from '../../lib/format';
import SyncJobsTable from './SyncJobsTable';

type SyncKey = 'users' | 'issues' | 'worklogs' | 'statusLog' | 'now';

export default function UpdateDataTab() {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState({ users: false, issues: false, worklogs: false, statusLog: false, now: false });

  // Success + live progress are driven by `sync_progress` websocket events
  // (see SyncProgressListener). Here we only surface errors as a fallback for
  // request-level failures that never produce a progress event.
  const setResult = (_key: SyncKey, msg: string) => {
    if (msg.startsWith('❌')) showToast('error', msg);
  };

  const handleUpdateUsers = async () => {
    setIsLoading(prev => ({ ...prev, users: true }));

    try {
      // Runs in the background; user groups are chained server-side after users finish.
      // Live progress + result arrive via the sync-progress toasts.
      const response = await apiService.syncUsers();
      if (!response.success) {
        setResult('users', `❌ Error: ${response.error}`);
      }
    } catch (error: any) {
      setResult('users', `❌ Error: ${error.message}`);
    } finally {
      setIsLoading(prev => ({ ...prev, users: false }));
    }
  };

  const handleUpdateIssues = async () => {
    setIsLoading(prev => ({ ...prev, issues: true }));
    try {
      // Check if issues table has data
      const countResp = await apiService.getIssuesCount();
      const count = Number(countResp?.data?.count || 0);
      const hasData = count > 0;

      if (hasData) {
        const ok = await confirm({
          title: 'Reload all issues?',
          message: 'This deletes all existing issues and imports everything again from Jira.',
          confirmLabel: 'Delete and re-import',
        });
        if (!ok) {
          return;
        }
        // Full replace
        const response = await apiService.syncIssuesTruncate();
        if (response.success) {
          setResult('issues', '✅ Issues fully reloaded successfully!');
        } else {
          setResult('issues', `❌ Error: ${response.error}`);
        }
      } else {
        // No data: fetch ALL ignoring incremental
        const response = await apiService.syncIssuesFull();
        if (response.success) {
          setResult('issues', '✅ Issues updated successfully!');
        } else {
          setResult('issues', `❌ Error: ${response.error}`);
        }
      }
    } catch (error: any) {
      setResult('issues', `❌ Error: ${error.message}`);
    } finally {
      setIsLoading(prev => ({ ...prev, issues: false }));
    }
  };

  const handleUpdateWorklogs = async () => {
    setIsLoading(prev => ({ ...prev, worklogs: true }));

    try {
      // Check if worklogs table has data
      const countResult = await apiService.getWorklogsCount();

      if (!countResult.success) {
        console.error('❌ Failed to get worklog count:', countResult.error);
        setResult('worklogs', `❌ Error getting worklog count: ${countResult.error}`);
        return;
      }

      const count = countResult.data?.count || 0;

      if (count > 0) {
        // Show confirmation popup
        const confirmed = await confirm({
          title: 'Reload all worklogs?',
          message: `This deletes the ${fmt.count(count)} existing worklogs first, then fetches every worklog again from Jira.`,
          confirmLabel: 'Delete and re-fetch',
        });

        if (confirmed) {
          const response = await apiService.syncWorklogsTruncate();
          if (response.success) {
            setResult('worklogs', '✅ Worklogs fully reloaded successfully!');
          } else {
            setResult('worklogs', `❌ Error: ${response.error}`);
          }
        }
      } else {
        // Table is empty, directly fetch all worklogs
        const response = await apiService.syncWorklogsFull();
        if (response.success) {
          setResult('worklogs', '✅ Worklogs updated successfully!');
        } else {
          setResult('worklogs', `❌ Error: ${response.error}`);
        }
      }
    } catch (error: any) {
      setResult('worklogs', `❌ Error: ${error.message}`);
    } finally {
      setIsLoading(prev => ({ ...prev, worklogs: false }));
    }
  };

  const handleSyncStatusLog = async () => {
    const ok = await confirm({
      title: 'Rebuild the status log?',
      message: 'This deletes all existing rows in the status log table and fetches them again from Jira.',
      confirmLabel: 'Rebuild',
    });
    if (!ok) return;
    setIsLoading(prev => ({ ...prev, statusLog: true }));
    try {
      const response = await apiService.syncStatusLog();
      if (response.success) {
        const d = response.data || {};
        setResult('statusLog', `✅ Status log synced. Baseline: ${d.baselineInserted}, Transitions: ${d.transitionsInserted}, Skipped: ${d.skipped}`);
      } else {
        setResult('statusLog', `❌ Error: ${response.error}`);
      }
    } catch (error: any) {
      setResult('statusLog', `❌ Error: ${error?.response?.data?.error || error.message}`);
    } finally {
      setIsLoading(prev => ({ ...prev, statusLog: false }));
    }
  };

  // What a scheduler would do, by hand: recent issue changes, then status moves, then worklogs, in the background.
  const handleUpdateNow = async () => {
    setIsLoading((prev) => ({ ...prev, now: true }));
    try {
      const response = await apiService.updateFromJiraNow();
      if (response.success) showToast('success', 'Updating from Jira. Each step shows in the table below.');
      else setResult('now', `❌ ${response.error}`);
    } catch (error: any) {
      setResult('now', `❌ ${error?.response?.data?.error || error.message}`);
    } finally {
      setIsLoading((prev) => ({ ...prev, now: false }));
    }
  };

  const Row = ({
    icon, iconBg, iconColor, title, description, buttonLabel, buttonColor, loading, onClick,
  }: {
    icon: React.ReactNode; iconBg: string; iconColor: string; title: string; description: string;
    buttonLabel: string; buttonColor: string; loading: boolean; onClick: () => void;
  }) => (
    <div className="flex items-center justify-between gap-4 p-4">
      <div className="flex items-center min-w-0">
        <div className={`p-2 rounded-lg ${iconBg}`}>
          <span className={iconColor}>{icon}</span>
        </div>
        <div className="ml-3 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <p className="text-xs text-gray-600">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={onClick}
          disabled={loading}
          className={`px-4 py-2 text-white text-xs rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${buttonColor}`}
        >
          {loading ? 'Working…' : buttonLabel}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Update data</h3>
        <p className="text-sm text-gray-600">Sync the latest data from your Jira instance</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        <Row icon={<RefreshCw className="w-5 h-5" />} iconBg="bg-sky-100" iconColor="text-sky-700"
          title="Update from Jira now" description="What changed lately: issues, then status moves, then worklogs"
          buttonLabel="Update now" buttonColor="bg-blue-700 hover:bg-blue-800"
          loading={isLoading.now} onClick={handleUpdateNow} />
        <Row icon={<Users className="w-5 h-5" />} iconBg="bg-blue-100" iconColor="text-blue-600"
          title="Update users" description="Sync user data from Jira"
          buttonLabel="Update users" buttonColor="bg-blue-600 hover:bg-blue-700"
          loading={isLoading.users} onClick={handleUpdateUsers} />
        <Row icon={<BarChart3 className="w-5 h-5" />} iconBg="bg-green-100" iconColor="text-green-600"
          title="Update issues" description="Sync issue data from Jira"
          buttonLabel="Update issues" buttonColor="bg-green-700 hover:bg-green-800"
          loading={isLoading.issues} onClick={handleUpdateIssues} />
        <Row icon={<Clock className="w-5 h-5" />} iconBg="bg-purple-100" iconColor="text-purple-600"
          title="Update worklogs" description="Force sync worklog data"
          buttonLabel="Update worklogs" buttonColor="bg-purple-600 hover:bg-purple-700"
          loading={isLoading.worklogs} onClick={handleUpdateWorklogs} />
        <Row icon={<Database className="w-5 h-5" />} iconBg="bg-indigo-100" iconColor="text-indigo-600"
          title="Sync status log" description="Rebuild issue status history from Jira"
          buttonLabel="Sync status log" buttonColor="bg-indigo-600 hover:bg-indigo-700"
          loading={isLoading.statusLog} onClick={handleSyncStatusLog} />
      </div>

      <SyncJobsTable />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">ℹ️ Information</h4>
        <div className="text-sm text-blue-700 space-y-1">
          <p>• <strong>Update from Jira now:</strong> Reads only what changed lately, so it is quick; press it before you send a report, or whenever the numbers look behind</p>
          <p>• <strong>Update users:</strong> Syncs user information from Jira</p>
          <p>• <strong>Update issues:</strong> Syncs issue data and statuses</p>
          <p>• <strong>Update worklogs:</strong> Forces a complete worklog refresh</p>
          <p>• <strong>Sync status log:</strong> Rebuilds the issue status history table</p>
        </div>
      </div>
    </div>
  );
}
